import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Loader2, Sparkles, Scan, Layers, X, Download, Share2, RotateCcw, Users, SwitchCamera, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import DiscoveryReveal from '../components/DiscoveryReveal';
import ARCardPicker, { cardImageUrl } from '../components/ar/ARCardPicker';
import ARCardOverlay from '../components/ar/ARCardOverlay';

const CARD_BASE_W = 200; // CSS px, base overlay width before scale
const CARD_BASE_H = 280; // 5:7 card ratio

// Higher index = rarer, matches backend rarity_system.RARITY_TIERS
const RARITY_RANK = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, mythic: 5 };

export default function ARPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const nav = useNavigate();

  const [streamOk, setStreamOk] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Mode
  const [mode, setMode] = useState('identify'); // 'identify' | 'field_team'

  // Camera facing — 'environment' (rear, default in Identify) or 'user' (front / selfie, default in Field-Team)
  const [facing, setFacing] = useState('environment');
  const streamRef = useRef(null);

  // Field-team state — supports up to 3 creatures in a single shot
  const MAX_CREATURES = 3;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]); // [{card, transform}]
  const [activeIdx, setActiveIdx] = useState(0);
  const [composite, setComposite] = useState(null); // { dataUrl, blob }
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [autoPickAttempted, setAutoPickAttempted] = useState(false);
  // 'creature' = transparent cutout (natural), 'card' = framed card
  const [overlayMode, setOverlayMode] = useState('creature');
  // Map of card_id -> transparent-cutout objectURL
  const [cutouts, setCutouts] = useState({});

  const selectedCard = selectedCards[activeIdx]?.card || null;

  const defaultTransformFor = (i) => ({
    // Fan the creatures out so they don't stack when adding new ones
    x: (i - 1) * 60,
    y: 40,
    scale: 1,
    rotate: 0,
    width: CARD_BASE_W,
    height: CARD_BASE_H,
  });

  const startStream = useCallback(async (nextFacing) => {
    try {
      // Stop any existing stream first so switching cameras works reliably
      streamRef.current?.getTracks().forEach(t => t.stop());
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: nextFacing } },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
      setStreamOk(true);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Camera access denied');
      setStreamOk(false);
    }
  }, []);

  useEffect(() => {
    startStream(facing);
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  // When entering Field-Team mode for the first time, flip to the front
  // (selfie) camera so the explorer is actually in the photo with their card.
  // When leaving Field-Team back to Identify, restore the environment cam.
  useEffect(() => {
    setFacing(mode === 'field_team' ? 'user' : 'environment');
  }, [mode]);

  // Auto-pick the user's Top Card on first entry to field-team mode
  useEffect(() => {
    if (mode !== 'field_team' || selectedCard || autoPickAttempted) return;
    setAutoPickAttempted(true);
    api.get('/cards', { params: { limit: 200 } })
      .then(({ data }) => {
        if (!data?.length) {
          setPickerOpen(true);
          return;
        }
        const sorted = [...data].sort((a, b) => {
          const ra = RARITY_RANK[a.rarity] ?? -1;
          const rb = RARITY_RANK[b.rarity] ?? -1;
          if (rb !== ra) return rb - ra;
          return String(b.created_at || '').localeCompare(String(a.created_at || ''));
        });
        setSelectedCards([{ card: sorted[0], transform: defaultTransformFor(1) }]);
        setActiveIdx(0);
      })
      .catch(() => setPickerOpen(true));
  }, [mode, selectedCard, autoPickAttempted]);

  // ---- Multi-creature selection helpers ----
  const addCard = (card) => {
    setSelectedCards(prev => {
      // If already selected, just activate it
      const existing = prev.findIndex(s => s.card.card_id === card.card_id);
      if (existing >= 0) { setActiveIdx(existing); return prev; }
      if (prev.length >= MAX_CREATURES) {
        // Replace the active slot when at max
        const next = [...prev];
        next[activeIdx] = { card, transform: defaultTransformFor(activeIdx + 1) };
        return next;
      }
      const nextIdx = prev.length;
      const next = [...prev, { card, transform: defaultTransformFor(nextIdx + 1) }];
      setActiveIdx(nextIdx);
      return next;
    });
    setPickerOpen(false);
  };

  const removeCardAt = (idx) => {
    setSelectedCards(prev => {
      const next = prev.filter((_, i) => i !== idx);
      setActiveIdx(a => {
        if (next.length === 0) return 0;
        if (idx < a) return a - 1;
        return Math.min(a, next.length - 1);
      });
      return next;
    });
  };

  const updateTransformAt = (idx, updater) => {
    setSelectedCards(prev => prev.map((slot, i) =>
      i === idx ? { ...slot, transform: typeof updater === 'function' ? updater(slot.transform) : updater } : slot
    ));
  };

  const captureIdentify = async () => {
    if (!videoRef.current || scanning) return;
    setScanning(true);
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      if (facing === 'user') {
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Frame capture failed');

      const form = new FormData();
      form.append('file', blob, 'ar-frame.jpg');
      if (navigator.geolocation) {
        await new Promise(res => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { form.append('latitude', pos.coords.latitude); form.append('longitude', pos.coords.longitude); res(); },
            () => res(), { timeout: 3500 }
          );
        });
      }
      const { data } = await api.post('/discover', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.success) setResult(data);
      else toast.error(data.ai?.error ? 'AI unavailable, try again' : 'Nothing recognisable in this frame');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Capture failed');
    } finally {
      setScanning(false);
    }
  };

  // Compose the video frame + card overlay + watermark into a single JPEG
  const captureFieldTeam = useCallback(async () => {
    if (!videoRef.current || !containerRef.current || selectedCards.length === 0 || scanning) return;
    setScanning(true);
    try {
      const video = videoRef.current;
      const rect = containerRef.current.getBoundingClientRect();

      // Render at the display resolution scaled by DPR for crispness (cap at 3)
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const outW = Math.round(rect.width * dpr);
      const outH = Math.round(rect.height * dpr);
      const canvas = canvasRef.current;
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');

      // 1) Draw the video frame with object-cover semantics matching the CSS.
      //    When using the front (selfie) camera we mirror the video pixels
      //    onto the canvas so the saved photo matches what the explorer
      //    just saw on-screen (Instagram-style WYSIWYG).
      const vw = video.videoWidth || outW;
      const vh = video.videoHeight || outH;
      const srcAspect = vw / vh;
      const dstAspect = outW / outH;
      let sx = 0, sy = 0, sW = vw, sH = vh;
      if (srcAspect > dstAspect) {
        // video is wider — crop sides
        sW = Math.round(vh * dstAspect);
        sx = Math.round((vw - sW) / 2);
      } else {
        // video is taller — crop top/bottom
        sH = Math.round(vw / dstAspect);
        sy = Math.round((vh - sH) / 2);
      }
      if (facing === 'user') {
        ctx.save();
        ctx.translate(outW, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, sx, sy, sW, sH, 0, 0, outW, outH);
        ctx.restore();
      } else {
        ctx.drawImage(video, sx, sy, sW, sH, 0, 0, outW, outH);
      }

      // 2) Draw each selected creature/card overlay in insertion order so
      //    the active one (last placed) sits above the earlier ones.
      for (const slot of selectedCards) {
        const card = slot.card;
        const t = slot.transform;
        const useCreature = overlayMode === 'creature' && !!cutouts[card.card_id];
        const overlaySrc = useCreature ? cutouts[card.card_id] : cardImageUrl(card);
        const cardImg = await loadImage(overlaySrc);
        const cx = (rect.width / 2 + t.x) * dpr;
        const cy = (rect.height / 2 + t.y) * dpr;
        const cw = t.width * t.scale * dpr;
        const ch = t.height * t.scale * dpr;
        const rad = (t.rotate * Math.PI) / 180;

        if (useCreature) {
          // Creature-only rendering: transparent PNG with a soft drop shadow
          // and a grounded contact shadow ellipse.
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rad);

          // Ground contact shadow (before the creature so it sits beneath)
          ctx.save();
          ctx.filter = 'blur(6px)';
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.beginPath();
          ctx.ellipse(0, ch * 0.44, cw * 0.28, ch * 0.03, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          // Creature with soft drop shadow
          if (cardImg) {
            ctx.shadowColor = 'rgba(0,0,0,0.5)';
            ctx.shadowBlur = 22 * dpr;
            ctx.shadowOffsetY = 12 * dpr;
            drawImageContain(ctx, cardImg, -cw / 2, -ch / 2, cw, ch);
          }
          ctx.restore();
        } else {
          // Framed card rendering (rarity border, rounded photo, labels)
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rad);
          // Card shadow
          ctx.shadowColor = 'rgba(0,0,0,0.55)';
          ctx.shadowBlur = 40 * dpr;
          ctx.shadowOffsetY = 10 * dpr;
          // Card border (rarity color) — draw slightly larger rounded rect behind
          const radius = 22 * dpr;
          const uiColor = rarityColor(card.rarity);
          roundRect(ctx, -cw / 2, -ch / 2, cw, ch, radius);
          ctx.fillStyle = uiColor;
          ctx.fill();
          ctx.shadowColor = 'transparent';

          // Inner photo — clip to rounded rect (2px padding for border effect)
          const pad = 3 * dpr;
          ctx.save();
          roundRect(ctx, -cw / 2 + pad, -ch / 2 + pad, cw - 2 * pad, ch - 2 * pad, radius - pad);
          ctx.clip();
          if (cardImg) {
            drawImageCover(ctx, cardImg, -cw / 2 + pad, -ch / 2 + pad, cw - 2 * pad, ch - 2 * pad);
          } else {
            ctx.fillStyle = '#1f1f1f';
            ctx.fillRect(-cw / 2 + pad, -ch / 2 + pad, cw - 2 * pad, ch - 2 * pad);
          }
          // Bottom gradient for legibility
          const grad = ctx.createLinearGradient(0, ch / 2 - 90 * dpr, 0, ch / 2);
          grad.addColorStop(0, 'rgba(0,0,0,0)');
          grad.addColorStop(1, 'rgba(0,0,0,0.85)');
          ctx.fillStyle = grad;
          ctx.fillRect(-cw / 2 + pad, ch / 2 - 90 * dpr - pad, cw - 2 * pad, 90 * dpr);
          // Rarity label (top-right)
          ctx.font = `bold ${9 * dpr}px ui-monospace, SFMono-Regular, Menlo, monospace`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'top';
          ctx.fillStyle = uiColor;
          ctx.fillText(String(card.rarity || 'common').toUpperCase(), cw / 2 - 12 * dpr, -ch / 2 + 10 * dpr);
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.textAlign = 'left';
          ctx.fillText('WILDQUEST', -cw / 2 + 12 * dpr, -ch / 2 + 10 * dpr);
          // Species name
          ctx.font = `600 ${13 * dpr}px "Fraunces", Georgia, serif`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'alphabetic';
          const speciesName = card.species_name || 'Wildlife';
          wrapText(ctx, speciesName, -cw / 2 + pad + 10 * dpr, ch / 2 - pad - 18 * dpr, cw - 2 * pad - 20 * dpr, 15 * dpr, 2);
          if (card.scientific_name) {
            ctx.font = `italic ${9 * dpr}px "Fraunces", Georgia, serif`;
            ctx.fillStyle = 'rgba(255,255,255,0.75)';
            ctx.fillText(card.scientific_name, -cw / 2 + pad + 10 * dpr, ch / 2 - pad - 6 * dpr);
          }
          ctx.restore(); // end clip
          ctx.restore(); // end card transform
        }
      }

      // 3) Watermark + field-notebook caption (bottom-left).
      //    Show the primary creature name plus a "+N" indicator if the
      //    explorer captured a multi-creature group shot.
      const primary = selectedCards[0]?.card || selectedCard;
      const extras = Math.max(0, selectedCards.length - 1);
      drawFieldNotebook(ctx, outW, outH, dpr, primary, extras);

      // 4) Encode
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
      setComposite({ dataUrl, blob });
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Composite failed');
    } finally {
      setScanning(false);
    }
  }, [selectedCards, selectedCard, scanning, facing, overlayMode, cutouts]);

  const [uploadedPhotoId, setUploadedPhotoId] = useState(null);
  const [postingToLodge, setPostingToLodge] = useState(false);

  // Uploads the composite to the cloud (idempotent per preview session).
  // Returns { photo_id, share_url } so callers can chain follow-up actions
  // like `Post to Lodge` without re-uploading the bytes.
  const uploadComposite = useCallback(async () => {
    if (!composite) return null;
    if (uploadedPhotoId) {
      // Already uploaded for this preview — reuse.
      return { photo_id: uploadedPhotoId, share_url: `/api/files/wildquest/ar-photos/*/${uploadedPhotoId}.jpg` };
    }
    const form = new FormData();
    form.append('file', composite.blob, 'field-team.jpg');
    // Attach the primary (first) creature's metadata for gallery display
    const primary = selectedCards[0]?.card || selectedCard;
    if (primary?.card_id) form.append('card_id', primary.card_id);
    if (primary?.species_name) {
      const extras = selectedCards.length > 1 ? ` +${selectedCards.length - 1}` : '';
      form.append('species_name', primary.species_name + extras);
    }
    const { data } = await api.post('/ar/field-team-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    setUploadedPhotoId(data.photo.photo_id);
    return { photo_id: data.photo.photo_id, share_url: data.share_url };
  }, [composite, selectedCard, selectedCards, uploadedPhotoId]);

  // Reset uploaded id whenever a new composite is captured
  useEffect(() => { setUploadedPhotoId(null); }, [composite?.dataUrl]);

  const downloadPhoto = () => {
    if (!composite) return;
    const a = document.createElement('a');
    a.href = composite.dataUrl;
    a.download = `wildquest-fieldteam-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Photo saved to your device');
  };

  const savePhotoToCloud = async () => {
    if (!composite || savingPhoto) return;
    setSavingPhoto(true);
    try {
      const { share_url } = await uploadComposite();
      const absolute = share_url.startsWith('http') ? share_url : `${window.location.origin}${share_url}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${selectedCard?.species_name || 'Wildlife'} · WildQuest Field-Team`,
            text: 'I spotted this one in the wild!',
            url: absolute,
          });
        } catch { /* user cancelled */ }
      } else {
        await navigator.clipboard.writeText(absolute).catch(() => {});
        toast.success('Share link copied to clipboard');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save to cloud');
    } finally {
      setSavingPhoto(false);
    }
  };

  const postComposteToLodge = async () => {
    if (!composite || postingToLodge) return;
    setPostingToLodge(true);
    try {
      const { photo_id } = await uploadComposite();
      await api.post(`/ar/photo/${photo_id}/post-to-lodge`);
      toast.success('Posted to the Lodge — explorers will see it on the Field-Team Wall');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post to Lodge');
    } finally {
      setPostingToLodge(false);
    }
  };

  const capture = mode === 'identify' ? captureIdentify : captureFieldTeam;
  const anyCutoutPending = mode === 'field_team' && overlayMode === 'creature'
    && selectedCards.some(s => !cutouts[s.card.card_id]);
  const canFieldTeamShoot = mode === 'field_team' && selectedCards.length > 0 && !anyCutoutPending;

  return (
    <div className="fixed inset-0 z-[80] bg-black" data-testid="ar-root" ref={containerRef}>
      {/* Video feed */}
      {streamOk ? (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: facing === 'user' ? 'scaleX(-1)' : 'none' }}
          muted
          playsInline
        />
      ) : error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 gap-3">
          <Camera className="w-8 h-8 text-red-400" />
          <p className="font-serif text-xl">Camera unavailable</p>
          <p className="text-sm text-muted-foreground max-w-xs">{error}</p>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-accent" />
        </div>
      )}

      {/* Identify reticle — only in identify mode */}
      {mode === 'identify' && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative">
            <ARFrame />
            <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-accent whitespace-nowrap">
              Point at wildlife
            </p>
          </div>
        </div>
      )}

      {/* Field-Team overlays — up to 3 creatures composited into one shot.
          Tap any overlay to make it active; only the active one shows
          selection glow. Cutouts are keyed per card_id and reported back
          via `onCutoutReady`. */}
      {mode === 'field_team' && selectedCards.map((slot, i) => (
        <ARCardOverlay
          key={slot.card.card_id}
          card={slot.card}
          transform={slot.transform}
          setTransform={(updater) => updateTransformAt(i, updater)}
          containerRef={containerRef}
          mode={overlayMode}
          active={i === activeIdx}
          onActivate={() => setActiveIdx(i)}
          onCutoutReady={(url) => setCutouts(c => (c[slot.card.card_id] === url ? c : { ...c, [slot.card.card_id]: url }))}
        />
      ))}

      {/* Field-Team hint */}
      {mode === 'field_team' && selectedCards.length > 0 && (
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="absolute inset-x-0 top-28 text-center text-[10px] uppercase tracking-[0.3em] text-accent pointer-events-none px-4"
          data-testid="ar-fieldteam-hint"
        >
          {facing === 'user'
            ? (selectedCards.length > 1 ? 'Get in frame · Arrange your team' : 'Get in frame · Drag your card into the shot')
            : (selectedCards.length > 1 ? 'Frame the scene · Arrange your team' : 'Point at wildlife · Place your card beside it')}
        </motion.p>
      )}

      {/* Scan line */}
      {scanning && mode === 'identify' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute inset-x-0 h-[3px] bg-gradient-to-b from-transparent via-accent to-transparent shadow-[0_0_40px_rgba(233,196,106,0.8)]"
            initial={{ top: '10%' }}
            animate={{ top: '90%' }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      )}

      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 z-40 flex flex-col gap-3 p-4 bg-gradient-to-b from-black/75 to-transparent pt-[max(env(safe-area-inset-top),1rem)]">
        <div className="flex items-center justify-between">
          <button
            onClick={() => nav(-1)}
            data-testid="ar-close"
            className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md">
            <Scan className="w-3 h-3 text-accent" />
            <span className="text-[10px] uppercase tracking-widest text-white">AR Mode · Beta</span>
          </div>
          <button
            onClick={() => setFacing(f => (f === 'user' ? 'environment' : 'user'))}
            data-testid="ar-flip-camera"
            className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20"
            aria-label={facing === 'user' ? 'Switch to rear camera' : 'Switch to selfie camera'}
            title={facing === 'user' ? 'Switch to rear camera' : 'Switch to selfie camera'}
          >
            <SwitchCamera className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Mode toggle — polished segmented control */}
        <div className="mx-auto flex items-center gap-1 rounded-full bg-black/50 border border-white/10 backdrop-blur-md p-1" role="tablist">
          <ModeTab
            active={mode === 'identify'}
            onClick={() => setMode('identify')}
            icon={<Scan className="w-3.5 h-3.5" />}
            label="Identify"
            testId="ar-mode-identify"
          />
          <ModeTab
            active={mode === 'field_team'}
            onClick={() => setMode('field_team')}
            icon={<Users className="w-3.5 h-3.5" />}
            label="Field-Team"
            testId="ar-mode-fieldteam"
          />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute inset-x-0 bottom-0 z-40 pb-8 pt-16 bg-gradient-to-t from-black/85 to-transparent flex flex-col items-center gap-3 pb-[max(env(safe-area-inset-bottom),2rem)]">
        {mode === 'identify' ? (
          <p className="text-xs text-white/70 max-w-[80%] text-center">
            Frame the subject inside the reticle, then tap to identify.
          </p>
        ) : (
          <div className="flex flex-col items-center gap-2 max-w-[95%]">
            {/* Creature dock — thumbnails for each selected creature, plus a
                Trash chip to remove the active one and a "+" chip to add
                another creature (up to 3 total). */}
            <div className="flex items-center gap-1.5 px-1.5 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-md">
              {selectedCards.map((slot, i) => {
                const img = cardImageUrl(slot.card);
                const isActive = i === activeIdx;
                return (
                  <button
                    key={slot.card.card_id}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`ar-creature-${i}`}
                    className={`relative w-10 h-10 rounded-full overflow-hidden ring-2 transition ${isActive ? 'ring-accent' : 'ring-white/20'}`}
                    aria-label={`Select ${slot.card.species_name}`}
                  >
                    {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                    {!cutouts[slot.card.card_id] && overlayMode === 'creature' && (
                      <span className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Loader2 className="w-3.5 h-3.5 text-accent animate-spin" />
                      </span>
                    )}
                  </button>
                );
              })}
              {selectedCards.length > 1 && (
                <button
                  onClick={() => removeCardAt(activeIdx)}
                  data-testid="ar-remove-active"
                  className="w-9 h-9 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center hover:bg-rose-500/25 transition"
                  aria-label="Remove active creature"
                  title="Remove active creature"
                >
                  <X className="w-4 h-4 text-rose-200" />
                </button>
              )}
              <button
                onClick={() => setPickerOpen(true)}
                data-testid="ar-add-creature"
                disabled={selectedCards.length >= MAX_CREATURES}
                className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 disabled:opacity-40 transition"
                aria-label={selectedCards.length >= MAX_CREATURES ? 'Team full — swap active' : 'Add another creature'}
                title={selectedCards.length >= MAX_CREATURES ? 'Team full — tap a creature and pick to swap' : 'Add another creature'}
              >
                <Layers className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-[10px] uppercase tracking-widest text-white/60 font-mono">
                {selectedCards.length}/{MAX_CREATURES} · tap creature to move
              </p>

              {/* Creature ↔ Card toggle — controls whether the overlays render
                  as background-removed creatures (natural composite) or the
                  framed battle cards. */}
              <div className="flex items-center rounded-full bg-black/50 border border-white/10 backdrop-blur-md p-0.5" role="tablist">
                <button
                  onClick={() => setOverlayMode('creature')}
                  data-testid="ar-overlay-creature"
                  className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${overlayMode === 'creature' ? 'bg-accent text-black' : 'text-white/70 hover:text-white'}`}
                  aria-selected={overlayMode === 'creature'}
                  role="tab"
                >
                  Creature
                </button>
                <button
                  onClick={() => setOverlayMode('card')}
                  data-testid="ar-overlay-card"
                  className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${overlayMode === 'card' ? 'bg-accent text-black' : 'text-white/70 hover:text-white'}`}
                  aria-selected={overlayMode === 'card'}
                  role="tab"
                >
                  Card
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={capture}
          disabled={!streamOk || scanning || (mode === 'field_team' && !canFieldTeamShoot)}
          data-testid="ar-shutter"
          className="w-20 h-20 rounded-full bg-white flex items-center justify-center ring-4 ring-white/30 disabled:opacity-40 active:scale-95 transition"
        >
          {scanning ? (
            <Loader2 className="w-7 h-7 text-black animate-spin" />
          ) : mode === 'field_team' ? (
            <Camera className="w-7 h-7 text-black" />
          ) : (
            <Sparkles className="w-7 h-7 text-black" />
          )}
        </button>
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <AnimatePresence>
        {result && (
          <DiscoveryReveal result={result} onClose={() => { setResult(null); nav('/dashboard'); }} />
        )}
      </AnimatePresence>

      {/* Field-Team picker sheet */}
      <ARCardPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedId={selectedCard?.card_id}
        onPick={addCard}
      />

      {/* Composite preview */}
      <AnimatePresence>
        {composite && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black flex flex-col"
            data-testid="ar-composite-preview"
          >
            <div className="flex items-center justify-between p-4 pt-[max(env(safe-area-inset-top),1rem)]">
              <button
                onClick={() => setComposite(null)}
                data-testid="ar-preview-close"
                className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <p className="text-[10px] uppercase tracking-[0.3em] text-accent">Field-Team Photo</p>
              <div className="w-9" />
            </div>
            <div className="flex-1 flex items-center justify-center p-4">
              <img
                src={composite.dataUrl}
                alt="Field-team composite"
                className="max-w-full max-h-full rounded-xl shadow-2xl"
              />
            </div>
            <div className="p-4 pb-[max(env(safe-area-inset-bottom),1rem)] flex flex-col items-center gap-2.5">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setComposite(null)}
                  data-testid="ar-retake"
                  className="flex items-center gap-2 px-4 py-3 rounded-full bg-white/10 border border-white/15 hover:bg-white/15 transition"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="text-sm">Retake</span>
                </button>
                <button
                  onClick={downloadPhoto}
                  data-testid="ar-save"
                  className="flex items-center gap-2 px-4 py-3 rounded-full bg-white text-black hover:bg-white/90 transition"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-semibold">Save</span>
                </button>
                <button
                  onClick={savePhotoToCloud}
                  disabled={savingPhoto}
                  data-testid="ar-share"
                  className="flex items-center gap-2 px-4 py-3 rounded-full bg-accent text-black hover:bg-accent/90 disabled:opacity-60 transition"
                >
                  {savingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  <span className="text-sm font-semibold">Share</span>
                </button>
              </div>
              <button
                onClick={postComposteToLodge}
                disabled={postingToLodge}
                data-testid="ar-post-lodge"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-60 transition font-semibold shadow-lg shadow-emerald-500/25"
              >
                {postingToLodge ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="text-sm">Post to Lodge</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Small internals ----------
function ARFrame() {
  return (
    <div className="w-64 h-64 relative">
      {[
        'top-0 left-0 border-t-2 border-l-2 rounded-tl-xl',
        'top-0 right-0 border-t-2 border-r-2 rounded-tr-xl',
        'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-xl',
        'bottom-0 right-0 border-b-2 border-r-2 rounded-br-xl',
      ].map((c, i) => (
        <motion.div
          key={i}
          className={`absolute w-10 h-10 border-accent ${c}`}
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

function ModeTab({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-medium transition ${active ? 'text-black' : 'text-white/70 hover:text-white'}`}
      role="tab"
      aria-selected={active}
    >
      {active && (
        <motion.span
          layoutId="ar-mode-pill"
          className="absolute inset-0 rounded-full bg-accent"
          transition={{ type: 'spring', damping: 25, stiffness: 320 }}
        />
      )}
      <span className="relative z-10 flex items-center gap-1.5">
        {icon}{label}
      </span>
    </button>
  );
}

// ---------- Canvas helpers ----------
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawImageCover(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const srcAspect = iw / ih;
  const dstAspect = dw / dh;
  let sx = 0, sy = 0, sW = iw, sH = ih;
  if (srcAspect > dstAspect) {
    sW = Math.round(ih * dstAspect);
    sx = Math.round((iw - sW) / 2);
  } else {
    sH = Math.round(iw / dstAspect);
    sy = Math.round((ih - sH) / 2);
  }
  ctx.drawImage(img, sx, sy, sW, sH, dx, dy, dw, dh);
}

// Draw an image scaled to FIT inside the destination rect (letterboxed),
// preserving the aspect ratio. Used for the creature cutout so the whole
// creature stays visible regardless of the source image aspect.
function drawImageContain(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const srcAspect = iw / ih;
  const dstAspect = dw / dh;
  let outW = dw, outH = dh;
  if (srcAspect > dstAspect) {
    outH = Math.round(dw / srcAspect);
  } else {
    outW = Math.round(dh * srcAspect);
  }
  const offX = dx + Math.round((dw - outW) / 2);
  const offY = dy + Math.round((dh - outH) / 2);
  ctx.drawImage(img, 0, 0, iw, ih, offX, offY, outW, outH);
}

function wrapText(ctx, text, x, yBaseline, maxWidth, lineHeight, maxLines = 2) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  // If we hit the cap and still have leftover words, ellipsize the last line
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + '…').width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last + (last.endsWith('…') ? '' : '');
  }
  const startY = yBaseline - (lines.length - 1) * lineHeight;
  lines.forEach((l, i) => ctx.fillText(l, x, startY + i * lineHeight));
}

function rarityColor(rarity) {
  return ({
    common: '#9CA3AF',
    uncommon: '#40916C',
    rare: '#5FA9E5',
    epic: '#B27BC7',
    legendary: '#E9C46A',
    mythic: '#FF6EC7',
  })[rarity] || '#9CA3AF';
}

function drawFieldNotebook(ctx, outW, outH, dpr, card, extras = 0) {
  const padX = 18 * dpr;
  const padY = 22 * dpr;
  const boxH = 62 * dpr;
  const boxW = Math.min(outW - padX * 2, 380 * dpr);
  const x = padX;
  const y = outH - padY - boxH - 28 * dpr; // sit above safe area

  ctx.save();
  // Glass panel
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  roundRect(ctx, x, y, boxW, boxH, 12 * dpr);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1 * dpr;
  ctx.stroke();

  // Accent bar
  ctx.fillStyle = '#E9C46A';
  ctx.fillRect(x, y, 3 * dpr, boxH);

  // Title (species) + subtitle (rarity · date)
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `600 ${12 * dpr}px "Fraunces", Georgia, serif`;
  ctx.fillStyle = '#ffffff';
  const speciesName = (card?.species_name || 'Wildlife') + (extras > 0 ? ` +${extras}` : '');
  ctx.fillText(truncate(ctx, speciesName, boxW - 20 * dpr), x + 12 * dpr, y + 10 * dpr);

  ctx.font = `${9 * dpr}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  const dateStr = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const label = String(card?.rarity || 'common').toUpperCase();
  ctx.fillText(`${label} · ${dateStr}`, x + 12 * dpr, y + 28 * dpr);

  // Watermark tag (right side)
  ctx.textAlign = 'right';
  ctx.font = `bold ${10 * dpr}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillStyle = '#E9C46A';
  ctx.fillText('WildQuest', x + boxW - 12 * dpr, y + 10 * dpr);
  ctx.font = `${8 * dpr}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.fillText('Field-Team', x + boxW - 12 * dpr, y + 26 * dpr);

  ctx.restore();
}

function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + '…').width > maxWidth) t = t.slice(0, -1);
  return t + '…';
}
